import { ComponentPropsWithoutRef, useCallback, useEffect, useRef } from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  Mode,
  Path,
  useForm,
  UseFormReturn,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { UseMutateFunction } from '@tanstack/react-query';
import { z } from 'zod';

import { Button, TButton } from '@/shared/ui/lib/Button';

import { ErrorDataObject } from '../../api/ApiError';
import { FormError } from '../FormError';

export type TFormMutation<T> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutate?: ((data: T) => void) | UseMutateFunction<any, any, any>;
  isPending: boolean;
  error?: { message: string; data?: ErrorDataObject } | null;
  reset: () => void;
};

type FormProps<T extends FieldValues> = Omit<
  ComponentPropsWithoutRef<'form'>,
  'children' | 'onSubmit'
> & {
  children:
    | ((methods: UseFormReturn<T>) => React.ReactNode)
    | React.ReactNode
    | React.ReactNode[];
  onSubmit?: (data: T) => void;
  onFieldChange?: (data: T) => void;
  schema?: z.ZodSchema<T>;
  defaultValues?: DefaultValues<T>;
  mode?: Mode;
  id?: string;
  mutation?: TFormMutation<T>;
  submitBtnText?: string;
  submitBtnProps?: TButton;
  sx?: React.CSSProperties;
};

export const Form = <T extends FieldValues>({
  children,
  onSubmit,
  schema,
  defaultValues,
  mode = 'onSubmit',
  mutation,
  id,
  submitBtnText,
  onFieldChange,
  submitBtnProps,
  sx,
  noValidate = false,
}: FormProps<T>) => {
  const formRef = useRef<HTMLFormElement | null>(null);
  const methods = useForm<T>({
    // resolvers v5 + zod 3.24 type variance — cast to bypass false-positive overload mismatch
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: schema ? zodResolver(schema as any) : undefined,
    defaultValues,
    mode,
  });

  const syncAutofilledFields = useCallback(() => {
    const form = formRef.current;
    if (!form) return;

    let hasSyncedField = false;
    const autofillableFields = form.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement
    >('input[name], textarea[name]');

    autofillableFields.forEach((field) => {
      if (
        field instanceof HTMLInputElement &&
        [
          'button',
          'checkbox',
          'file',
          'hidden',
          'radio',
          'reset',
          'submit',
        ].includes(field.type)
      ) {
        return;
      }

      const name = field.name as Path<T>;
      const { value } = field;
      if (!name || !value) return;

      const currentValue = methods.getValues(name);
      if (
        currentValue !== undefined &&
        currentValue !== null &&
        currentValue !== ''
      ) {
        return;
      }

      methods.setValue(name, value as never, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      hasSyncedField = true;
    });

    if (hasSyncedField) {
      void methods.trigger();
    }
  }, [methods]);

  useEffect(() => {
    const timers = [0, 100, 500].map((delay) =>
      window.setTimeout(syncAutofilledFields, delay)
    );

    window.addEventListener('focus', syncAutofilledFields);
    window.addEventListener('pageshow', syncAutofilledFields);

    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      window.removeEventListener('focus', syncAutofilledFields);
      window.removeEventListener('pageshow', syncAutofilledFields);
    };
  }, [syncAutofilledFields]);

  useEffect(() => {
    if (!onFieldChange) return;

    const subscription = methods.watch((value, { name, type }) => {
      if (name && type === 'change') {
        onFieldChange(methods.getValues());
      }
    });

    return () => subscription.unsubscribe();
  }, [methods, onFieldChange]);

  useEffect(() => {
    if (!mutation?.error) return;

    const subscription = methods.watch(() => {
      mutation.reset();
    });

    return () => subscription.unsubscribe();
  }, [methods, mutation, mutation?.error]);

  const handleSubmit = (data: T) => {
    if (mutation && !onSubmit) {
      mutation?.mutate?.(data);
    } else if (onSubmit) {
      onSubmit(data);
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        ref={formRef}
        onSubmit={methods.handleSubmit(handleSubmit)}
        id={id}
        sx={sx}
        noValidate={noValidate}
      >
        {typeof children === 'function' ? children(methods) : children}
        {mutation?.error && (
          <FormError
            message={mutation?.error.message}
            details={mutation?.error.data?.details}
          />
        )}

        {submitBtnText ? (
          <Box display="flex">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!methods.formState.isValid || mutation?.isPending}
              fullWidth
              sx={{ mb: 2, mt: 2 }}
              loading={mutation?.isPending}
              {...submitBtnProps}
            >
              {submitBtnText}
            </Button>
          </Box>
        ) : null}
      </Box>
    </FormProvider>
  );
};
